import {
  FC,
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useTheme } from "injectors/theme";
import { useTranslation } from "@kolibrisoftware/hooks-util";
import { useStyles } from "./styles";
import { ContentSection, Button } from "@kolibrisoftware/customerportal-ui";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { ObjectTabTargets } from "modules/object-preview/constants/object-tabs";
import { TranslatedText } from "@kolibrisoftware/customerportal-api";

export const DESCRIPTION_MAX_HEIGHT = 150;
type Props = {
  content: TranslatedText[] | null | undefined;
  title: ReactNode;
};

const ObjectDescriptionSection: FC<Props> = ({ content, title }: Props) => {
  const theme = useTheme();
  const styles = useStyles({ theme });
  const t = useTranslation();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showCollapsibleAction, setShowCollapsibleAction] = useState(false);

  // It's an useEffect controlling state because
  // useMemo doesn't register changes to refs
  useEffect(() => {
    const contentHeight = contentRef.current?.getBoundingClientRect().height;
    if (contentHeight && contentHeight > DESCRIPTION_MAX_HEIGHT) {
      setShowCollapsibleAction(true);
      return;
    }
    setShowCollapsibleAction(false);
  }, [contentRef]);

  const toggleSection = useCallback(() => {
    setIsCollapsed(prevState => !prevState);
  }, []);

  if (!content) {
    return null;
  }

  return (
    <div
      className={styles.descriptionSectionWrapper}
      id={ObjectTabTargets.Description}
      data-cy="ObjectDescription"
    >
      {!!content && (
        <ContentSection title={title} style={{ marginBottom: "unset" }}>
          <div
            className={classNames(styles.descriptionContentWrapper, {
              [styles.collapsedDescription]: isCollapsed,
            })}
          >
            <div className={styles.descriptionContent} ref={contentRef}>
              {content?.map((description, index) => {
                return (
                  <div key={index} className={styles.descriptionRow}>
                    {description.text}
                  </div>
                );
              })}
            </div>
          </div>

          {showCollapsibleAction && (
            <div className={styles.collapsibleActionWrapper}>
              {isCollapsed && (
                <div className={styles.collapsibleActionGradient} />
              )}
              <Button
                data-cy="ObjectDescriptionCollapsible"
                onClick={toggleSection}
                icon={isCollapsed ? faPlus : faMinus}
                label={
                  isCollapsed
                    ? t("objectPreview.description.expandSection")
                    : t("objectPreview.description.collapseSection")
                }
              />
            </div>
          )}
        </ContentSection>
      )}
    </div>
  );
};

export { ObjectDescriptionSection };
